<template>
  <div v-if="isNewBvcInput" class="b-my-2 mx-0.5">
    <bvc-textarea
      v-if="useTextarea"
      ref="input"
      v-bind="bindings"
      :rows="lines"
      :style="[{ minWidth, textTransform: currentValue && textTransform }]"
      v-model:model-value="currentValue"
      :data-testid="testId"
      :clearable="showClear"
      @clear="onClear"
      @focus="focus"
      @blur="blur"
      @keyup="onKeyup"
      @click="onInputClick"
    />
    <bvc-input
      v-else
      ref="input"
      v-bind="bindings"
      :style="[{ minWidth, textTransform: currentValue && textTransform }]"
      :data-testid="testId"
      v-model:model-value="currentValue"
      :clearable="showClear"
      @clear="onClear"
      @focus="focus"
      @blur="blur"
      @keyup="onKeyup"
      @click="onInputClick"
    >
      <template v-if="$slots.left" #addonBefore>
        <slot name="left" v-bind="slotBindings" />
      </template>
      <template v-if="$slots.right" #addonAfter>
        <slot name="right" v-bind="slotBindings" />
      </template>
    </bvc-input>
    <div
      v-if="useTextarea && maxCharacters"
      class="text-end"
      data-testid="char-count"
    >
      {{ charCount }}
    </div>
  </div>
  <div v-else :class="cssClasses" @click="onContainerClick">
    <div :class="$style.outerWrapper" :style="{ minWidth: minWidth + 'px' }">
      <div :class="$style.labelWrapper" v-if="showLabel">
        <label :class="$style.label">
          {{ label }}
        </label>
      </div>

      <div :class="$style.wrapper">
        <div v-if="$slots.left" :class="[$slots.left && $style.slotLeft]">
          <slot name="left" v-bind="slotBindings" />
        </div>

        <textarea
          v-if="useTextarea"
          v-autosize="autosize"
          v-bind="bindings"
          :rows="lines"
          :class="$style.field"
          :value="currentValue"
          @input="onInput"
          @focus="focus"
          @blur="blur"
          @keyup="onKeyup"
          @click="onInputClick"
          :placeholder="computedPlaceholder"
          :maxlength="maxLength"
          :data-testid="testId"
          ref="input"
        />
        <input
          v-else
          v-bind="bindings"
          :class="$style.field"
          :style="[{ minWidth, textTransform: value && textTransform }]"
          :value="currentValue"
          @input="onInput"
          @focus="focus"
          @blur="blur"
          @keyup="onKeyup"
          @click="onInputClick"
          :placeholder="computedPlaceholder"
          :maxlength="maxLength"
          :data-testid="testId"
          ref="input"
        />

        <i
          v-if="showClear"
          :class="[$style.clearIcon, useTextarea ? $style.positionTop : $style.positionCenter]"
          @click.stop="onClear"
          @mousedown.prevent
        />

        <div v-if="$slots.right" :class="[$slots.right && $style.slotRight]">
          <slot name="right" v-bind="slotBindings"/>
        </div>
      </div>

      <div
        v-if="useTextarea && maxCharacters"
        :class="$style.counter"
        data-testid="char-count"
      >
        {{ charCount }}
      </div>
    </div>
    <div
      v-if="!useTextarea && maxCharacters && !invalid"
      :class="[$style.counter, maxCharactersReached && $style.counterMax]"
      data-testid="char-count"
    >
      {{ charCount }}
    </div>
  </div>
</template>

<script>
/*
 *
 * b-input component
 *
 */
import { is } from 'ramda'
import autosize from '../../directives/autosize'

import Input from '../../mixins/input.js'
import InputSize from '../../mixins/input-size.js'
import VModelInterface from '../../mixins/v-model-interface.js'

// themeable mixin factory
import makeThemeable from '../../utils/make-themeable.js'

import { useEppo } from '@/composables/eppo'
import { FEATURE_FLAGS } from '@/lib/consts.ts'

import { BInput as BvcInput, BTextarea as BvcTextarea } from '@booksy-com/vue-components-core'

export const THEME_TYPES = [
  'default',
  'blank',
  'blank--no-h-padding',
  'filled',
]

export const SLOT_THEME_TYPES = [
  'default',
  'none',
  'blank',
  'filled',
]

const SUPPORTED_TYPES = [
  'text',
  'url',
  'email',
  'password',
  'tel',
  'search',
  'number',
]

const INPUTMODE_TYPES = [
  'text',
  'numeric',
  'tel',
  'decimal',
  'email',
  'url',
  'search',
  'none',
]

const AUTOCOMPLETE_TYPES = [
  'on',
  'off',
  'name',
  'email',
  'tel',
  'url',
  'photo',
  'none',
  'tel-national',
  'address-line1',
  'address-line2',
  'street-address',
]

const TEXT_STYLES = [
  'none',
  'lowercase',
  'capitalize',
  'uppercase',
]

// custom themeable mixin (prop. theme)
const Themeable = makeThemeable(THEME_TYPES)

// custom themeable mixin for slot (prop. slotTheme)
const ThemeableSlots = makeThemeable(SLOT_THEME_TYPES, 'slotTheme')

export default {

  emits: [
    'blur',
    'change',
    'clear',
    'click',
    'focus',
    'input',
    'keyup',
    'update:modelValue',
  ],
  name: 'b-input',

  directives: {
    autosize,
  },

  mixins: [
    Input,
    InputSize,
    VModelInterface,
    Themeable,
    ThemeableSlots,
  ],

  props: {

    type: {
      type: String,
      default: SUPPORTED_TYPES[0],
      validator: (value) => SUPPORTED_TYPES.includes(value),
    },

    label: {
      type: String,
      default: '',
    },

    lines: {
      type: Number,
      default: 1,
    },

    placeholder: {
      type: String,
      default: null,
    },

    autocomplete: {
      type: String,
      default: AUTOCOMPLETE_TYPES[0],
      validator: (value) => AUTOCOMPLETE_TYPES.includes(value),
    },

    inputmode: {
      type: String,
      default: INPUTMODE_TYPES[0],
      validator: (value) => INPUTMODE_TYPES.includes(value),
    },

    textTransform: {
      type: String,
      default: TEXT_STYLES[0],
      validator: (value) => TEXT_STYLES.includes(value),
    },

    // enforces active state
    active: {
      type: Boolean,
      default: null,
    },

    autosize: {
      type: Boolean,
      default: null,
    },

    hasClear: {
      type: Boolean,
      default: true,
    },

    minWidth: {
      type: Number,
      default: 0,
    },

    maxLength: {
      type: Number,
      default: null,
    },

    maxCharacters: Number,

    // prevent losing focus on clicks (e.g. in bvc-select)
    keepFocus: {
      type: Boolean,
      default: false,
    },

    role: {
      type: String,
      default: null,
    },

    modelValue: {
      type: String,
      default: '',
    },
  },

  components: {
    BvcInput,
    BvcTextarea,
  },

  data () {
    return {
      focused: false,
    }
  },

  computed: {
    isNewBvcInput () {
      const eppo = useEppo()
      return eppo?.ff(FEATURE_FLAGS.Refactor_BvcInput)
    },

    useTextarea () {
      return this.autosize || this.lines > 1
    },

    bindings () {
      // @see input mixin
      const bindings = this.input__bindings
      bindings.type = this.type
      bindings.role = this.role
      bindings.autocomplete = this.autocomplete
      bindings.inputmode = this.inputmode
      bindings.placeholder = this.placeholder || this.label
      bindings.maxlength = this.maxLength
      bindings.size = this.size

      bindings.state = this.invalid ? 'invalid' : 'default'

      return bindings
    },

    cssClasses () {
      const classes = [
        this.$style.container,
        this.isFocused && this.$style.focused,
        this.isFocusedOrFilled && this.$style.focusedOrFilled,
      ]

      /*
       * if (this.$slots.left) {
       *   classes.push(this.$style['has--leftSlot'])
       * }
       *
       * if (this.$slots.right) {
       *   classes.push(this.$style['has--rightSlot'])
       * }
       */

      return classes.concat(
        // @see input mixin
        this.input__cssClasses,
        // @see input-size mixin
        this.size__cssClasses,
        // @see themeable mixin (factory)
        this.theme__cssClasses,
        // @see themeable mixin (factory)
        this.slotTheme__cssClasses
      )
    },

    /*
     * bindings for left & right slot
     * @return Object
     */
    slotBindings () {
      return {
        readonly: this.readonly,
        disabled: this.disabled,
        invalid: this.invalid,
        warning: this.warning,
        focused: this.isFocused,
        filled: this.isFilled,
      }
    },

    isFocused () {
      if (is(Boolean, this.active)) {
        return this.active
      }

      return this.focused
    },

    isFilled () {
      // WARNING: we're not sure why this was this way
      // i'll leave it as a warning here
      // return !!String(this.value)
      let value = this.currentValue

      if (value === 0) return true
      return !!value
    },

    isFocusedOrFilled () {
      return this.isFocused || this.isFilled
    },

    computedPlaceholder () {
      if (!this.label) {
        return this.placeholder
      }

      if (this.placeholder) {
        if (this.label) {
          return this.isFocusedOrFilled ? this.placeholder : this.label
        } else {
          return this.placeholder
        }
      }

      return this.isFocusedOrFilled ? '' : this.label
    },

    showLabel () {
      if (this.theme === 'blank') {
        return false
      }

      return !!this.label
    },

    showClear () {
      /*
       * shortcut: if "disabled" or "readonly" value can not be
       *           changed, including "clear"
       */
      if (this.disabled || this.readonly) return false

      if (this.type === 'search' && this.isFilled) return true

      if (this.isNewBvcInput) {
        return this.hasClear && this.isFilled
      }

      return this.hasClear && this.isFilled && this.focused
    },

    charCount () {
      return `${this.currentValue?.length || 0}/${this.maxCharacters}`
    },

    maxCharactersReached () {
      return (this.currentValue?.length || 0) >= this.maxCharacters
    },

    testId () {
      return this.name || undefined
    },

    currentValue: {
      get () {
        if (this.isNewBvcInput) {
          return this.value || this.modelValue || ''
        }
        return this.value
      },
      set (value) {
        if (this.isNewBvcInput) {
          this.$emit('update:modelValue', value)
          // emits to support compat mode - the same as this.emitInput
          this.$emit('input', value)
          this.$emit('change', value)
        } else {
          this.emitInput(value)
        }
      },
    },
  },

  methods: {

    /*
     *
     * public interface
     *
     */
    focus (e) {
      if (this.disabled || this.readonly) return false
      this.focused = true
      this.$refs.input.focus()
      this.$emit('focus', e)
    },

    blur (e) {
      this.focused = false
      this.$nextTick(() => {
        if (this.isNewBvcInput) {
          this.$emit('blur', e)
          return
        }
        this.$refs.input?.blur()
      })
    },

    select () {
      if (this.isNewBvcInput) {
        return
      }
      this.$refs.input.select()
    },

    /*
     *
     * private
     *
     */
    onInput (e) {
      // @see v-model-interface mixin
      this.emitInput(e.target.value)
    },

    onKeyup (e) {
      this.$emit('keyup', e)
    },

    onClear () {
      this.$emit('clear')
      this.currentValue = ''
    },

    onInputClick (event) {
      if (this.isNewBvcInput) {
        this.$emit('click', event)
      }
      if (this.keepFocus) {
        event.stopPropagation()
      }
    },

    onContainerClick (e) {
      this.$refs.input.focus()
      this.$emit('click', e)
    },
  },
}
</script>

<style src="@/booksy-vue-components/components/b-input/styles.scss" lang="scss" module />
